import React, { useState, useRef, useCallback } from 'react';
import { Play, Pause, RotateCcw, Plus, Minus } from 'lucide-react';

// ── Audio Engine ────────────────────────────────────────────────────────────
// iOS silences Web Audio API output that goes straight to ctx.destination
// whenever the hardware mute/silent switch is on. Routing the synth graph
// through a MediaStreamDestination and playing it via a hidden <audio>
// element puts it on the "media playback" channel instead, which iOS does
// not mute with the switch.
const BELL_URL = `${process.env.PUBLIC_URL}/sounds/boxing-bell.mp3`;

const makeAudio = () => {
  let ctx = null;
  let out = null; // AudioNode to connect synth output to
  let el = null;
  let bellBuffer = null; // decoded real bell recording, once loaded

  const ac = () => {
    if (!ctx) {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      try {
        const dest = ctx.createMediaStreamDestination();
        out = dest;
        el = new Audio();
        el.srcObject = dest.stream;
        el.playsInline = true;
        el.autoplay = true;
        el.volume = 1;
      } catch (e) {
        out = ctx.destination; // fallback if MediaStreamDestination unsupported
      }
      fetch(BELL_URL)
        .then((r) => r.arrayBuffer())
        .then((data) => ctx.decodeAudioData(data))
        .then((buf) => { bellBuffer = buf; })
        .catch(() => { /* keep synthesized fallback */ });
    }
    if (ctx.state === 'suspended') ctx.resume();
    if (el && el.paused) el.play().catch(() => {});
    return ctx;
  };

  // Synthesized bell, used until the real recording has finished loading
  const synthBell = (c, t) => {
    // Noise burst for sharp attack
    try {
      const bufLen = Math.floor(c.sampleRate * 0.025);
      const buf = c.createBuffer(1, bufLen, c.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < bufLen; i++) d[i] = (Math.random() * 2 - 1);
      const ns = c.createBufferSource();
      ns.buffer = buf;
      const ng = c.createGain();
      ns.connect(ng); ng.connect(out);
      ng.gain.setValueAtTime(0.35, t);
      ng.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
      ns.start(t); ns.stop(t + 0.03);
    } catch (e) { /* ignore */ }

    // Harmonics for ring
    [[440, 1.0, 3.5], [880, 0.5, 2.5], [1320, 0.2, 1.5], [220, 0.4, 4.0]].forEach(([f, v, dur]) => {
      const o = c.createOscillator();
      const g = c.createGain();
      o.connect(g); g.connect(out);
      o.type = 'sine';
      o.frequency.value = f;
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(v, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      o.start(t); o.stop(t + dur);
    });
  };

  const bell = (delay = 0) => {
    const c = ac();
    const t = c.currentTime + delay;

    if (bellBuffer) {
      const src = c.createBufferSource();
      src.buffer = bellBuffer;
      src.connect(out);
      src.start(t);
    } else {
      synthBell(c, t);
    }
  };

  const beep = (freq = 880, dur = 0.13) => {
    const c = ac();
    const o = c.createOscillator();
    const g = c.createGain();
    o.connect(g); g.connect(out);
    o.type = 'sine';
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.45, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
    o.start(c.currentTime);
    o.stop(c.currentTime + dur);
  };

  const tecTec = () => {
    const c = ac();
    for (let i = 0; i < 3; i++) {
      const t = c.currentTime + i * 0.18;
      const o = c.createOscillator();
      const g = c.createGain();
      o.connect(g); g.connect(out);
      o.type = 'square';
      o.frequency.value = 1700;
      g.gain.setValueAtTime(0.4, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.07);
      o.start(t); o.stop(t + 0.07);
    }
  };

  const complete = () => {
    bell(0); bell(0.9); bell(1.8);
  };

  return { bell, beep, tecTec, complete };
};

const audio = makeAudio();

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

// ── Status constants ─────────────────────────────────────────────────────────
const S = { IDLE: 'IDLE', COUNTDOWN: 'COUNTDOWN', WORK: 'WORK', REST: 'REST', DONE: 'DONE' };

// ── Config Box ───────────────────────────────────────────────────────────────
function ConfigBox({ label, display, onDec, onInc, testId }) {
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4 flex flex-col items-center gap-3">
      <span
        className="text-xs font-bold tracking-[0.2em] uppercase text-gray-400"
        style={{ fontFamily: 'DM Sans, sans-serif' }}
      >
        {label}
      </span>
      <span
        className="text-3xl sm:text-4xl font-black tabular-nums text-white"
        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        data-testid={`${testId}-value`}
      >
        {display}
      </span>
      <div className="flex gap-3">
        <button
          onClick={onDec}
          data-testid={`${testId}-dec`}
          className="bg-[#2C2C2E] hover:bg-[#3A3A3C] active:scale-95 text-white p-2.5 rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-[#FF3B30]"
          aria-label={`Diminuir ${label}`}
        >
          <Minus size={18} />
        </button>
        <button
          onClick={onInc}
          data-testid={`${testId}-inc`}
          className="bg-[#2C2C2E] hover:bg-[#3A3A3C] active:scale-95 text-white p-2.5 rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-[#FF3B30]"
          aria-label={`Aumentar ${label}`}
        >
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function BoxingTimer() {
  const [cfg, setCfg] = useState({ rounds: 3, work: 120, rest: 40 });
  const [status, setStatus] = useState(S.IDLE);
  const [timeLeft, setTimeLeft] = useState(0);
  const [round, setRound] = useState(0);
  const [paused, setPaused] = useState(false);

  // All mutable runtime state in a single ref — no stale closures
  const rt = useRef({ status: S.IDLE, timeLeft: 0, round: 0, cfg: { rounds: 3, work: 120, rest: 40 }, tecFired: false });
  const timerId = useRef(null);
  const expected = useRef(null);

  const stopTimer = () => {
    clearTimeout(timerId.current);
    timerId.current = null;
  };

  // Tick function stored in a ref so it always has latest state
  const tickRef = useRef(null);
  tickRef.current = () => {
    const state = rt.current;
    const newTime = state.timeLeft - 1;

    if (state.status === S.COUNTDOWN) {
      if (newTime > 0) {
        audio.beep(newTime <= 2 ? 1100 : 880);
        state.timeLeft = newTime;
        setTimeLeft(newTime);
      } else {
        // Countdown finished → start round 1
        audio.bell();
        state.status = S.WORK;
        state.round = 1;
        state.tecFired = false;
        state.timeLeft = state.cfg.work;
        setStatus(S.WORK);
        setRound(1);
        setTimeLeft(state.cfg.work);
      }
    } else if (state.status === S.WORK) {
      if (newTime === 15 && !state.tecFired) {
        state.tecFired = true;
        audio.tecTec();
      }
      if (newTime > 0) {
        state.timeLeft = newTime;
        setTimeLeft(newTime);
      } else {
        audio.bell();
        if (state.round >= state.cfg.rounds) {
          // All rounds finished
          state.status = S.DONE;
          setStatus(S.DONE);
          stopTimer();
          setTimeout(() => audio.complete(), 1800);
          return;
        }
        // Go to rest
        state.status = S.REST;
        state.timeLeft = state.cfg.rest;
        setStatus(S.REST);
        setTimeLeft(state.cfg.rest);
      }
    } else if (state.status === S.REST) {
      if (newTime > 0) {
        state.timeLeft = newTime;
        setTimeLeft(newTime);
      } else {
        // Rest done → start next round
        const next = state.round + 1;
        audio.bell();
        state.status = S.WORK;
        state.round = next;
        state.tecFired = false;
        state.timeLeft = state.cfg.work;
        setStatus(S.WORK);
        setRound(next);
        setTimeLeft(state.cfg.work);
      }
    }

    // Drift-corrected reschedule
    if (rt.current.status !== S.DONE) {
      const drift = Date.now() - expected.current;
      expected.current += 1000;
      timerId.current = setTimeout(() => tickRef.current(), Math.max(0, 1000 - drift));
    }
  };

  const startInterval = () => {
    expected.current = Date.now() + 1000;
    timerId.current = setTimeout(() => tickRef.current(), 1000);
  };

  const handleStart = useCallback(() => {
    const state = rt.current;
    state.cfg = cfg;
    state.status = S.COUNTDOWN;
    state.round = 0;
    state.timeLeft = 5;
    state.tecFired = false;
    setStatus(S.COUNTDOWN);
    setRound(0);
    setTimeLeft(5);
    setPaused(false);
    audio.beep(880); // immediate beep for "5"
    startInterval();
  }, [cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePause = useCallback(() => {
    stopTimer();
    setPaused(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResume = useCallback(() => {
    setPaused(false);
    expected.current = Date.now() + 1000;
    timerId.current = setTimeout(() => tickRef.current(), 1000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReset = useCallback(() => {
    stopTimer();
    rt.current.status = S.IDLE;
    setStatus(S.IDLE);
    setTimeLeft(0);
    setRound(0);
    setPaused(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const adjustCfg = (key, delta, min, max) =>
    setCfg(prev => ({ ...prev, [key]: Math.max(min, Math.min(max, prev[key] + delta)) }));

  const isActive = status !== S.IDLE && status !== S.DONE;

  const timerColor =
    status === S.WORK
      ? timeLeft <= 15
        ? 'text-[#FFD60A]'
        : 'text-[#FF3B30]'
      : status === S.REST
      ? 'text-[#32D74B]'
      : 'text-white';

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white flex flex-col relative overflow-hidden"
      style={{ fontFamily: 'DM Sans, sans-serif' }}
    >
      {/* Subtle background image */}
      <div
        className="absolute inset-0 opacity-[0.06] bg-cover bg-center pointer-events-none"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1561532325-7d5231a2dede?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85')" }}
      />

      <div className="relative z-10 flex flex-col min-h-screen select-none">

        {/* ── Header ──────────────────────────────────────────────────── */}
        <header className="text-center pt-7 pb-2">
          <h1
            className="text-xl font-black tracking-[0.45em] uppercase text-white/50"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            BOXING TIMER
          </h1>
        </header>

        {/* ── Config Panel (only in IDLE) ─────────────────────────────── */}
        {status === S.IDLE && (
          <section className="px-4 py-5" data-testid="config-panel">
            <div className="grid grid-cols-3 gap-3 max-w-xl mx-auto">
              <ConfigBox
                label="Rounds"
                display={String(cfg.rounds)}
                testId="rounds"
                onDec={() => adjustCfg('rounds', -1, 1, 20)}
                onInc={() => adjustCfg('rounds', 1, 1, 20)}
              />
              <ConfigBox
                label="Round"
                display={fmt(cfg.work)}
                testId="work"
                onDec={() => adjustCfg('work', -30, 30, 1800)}
                onInc={() => adjustCfg('work', 30, 30, 1800)}
              />
              <ConfigBox
                label="Descanso"
                display={fmt(cfg.rest)}
                testId="rest"
                onDec={() => adjustCfg('rest', -10, 10, 600)}
                onInc={() => adjustCfg('rest', 10, 10, 600)}
              />
            </div>
          </section>
        )}

        {/* ── Round Progress Dots ─────────────────────────────────────── */}
        {isActive && (
          <div className="flex justify-center gap-2.5 py-3" data-testid="round-dots">
            {Array.from({ length: cfg.rounds }, (_, i) => i + 1).map(n => (
              <div
                key={n}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  n < round
                    ? 'bg-[#FF3B30] opacity-60'
                    : n === round && status === S.WORK
                    ? 'bg-[#FF3B30] scale-125'
                    : n === round && status === S.REST
                    ? 'bg-[#32D74B] scale-125'
                    : 'bg-white/15'
                }`}
              />
            ))}
          </div>
        )}

        {/* ── Timer Center ────────────────────────────────────────────── */}
        <main className="flex-1 flex flex-col items-center justify-center px-4 -mt-4">

          {/* IDLE ghost preview */}
          {status === S.IDLE && (
            <div className="text-center mt-4">
              <div
                className="font-black tabular-nums leading-none text-white/15"
                style={{ fontSize: 'clamp(72px, 26vw, 280px)', fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {fmt(cfg.work)}
              </div>
              <p className="text-gray-600 text-xs tracking-[0.3em] uppercase mt-3">
                configure e inicie
              </p>
            </div>
          )}

          {/* COUNTDOWN */}
          {status === S.COUNTDOWN && (
            <div className="text-center" data-testid="countdown-display">
              <p
                className="text-base font-bold tracking-[0.35em] uppercase text-gray-400 mb-1"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                PREPARE-SE
              </p>
              <div
                className="font-black tabular-nums leading-none text-white"
                style={{ fontSize: 'clamp(130px, 42vw, 360px)', fontFamily: 'Barlow Condensed, sans-serif' }}
                data-testid="countdown-number"
              >
                {timeLeft}
              </div>
            </div>
          )}

          {/* WORK or REST */}
          {(status === S.WORK || status === S.REST) && (
            <div className="text-center w-full" data-testid="timer-section">
              <p
                className={`font-bold tracking-[0.3em] uppercase mb-1 ${
                  status === S.WORK ? 'text-[#FF3B30]' : 'text-[#32D74B]'
                }`}
                style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: 'clamp(18px, 4vw, 36px)',
                }}
                data-testid="status-label"
              >
                {status === S.WORK ? `ROUND ${round} / ${cfg.rounds}` : 'DESCANSO'}
              </p>
              <div
                className={`font-black tabular-nums leading-none transition-colors duration-300 ${timerColor}`}
                style={{ fontSize: 'clamp(80px, 28vw, 320px)', fontFamily: 'Barlow Condensed, sans-serif' }}
                data-testid="timer-display"
              >
                {fmt(timeLeft)}
              </div>
              {status === S.WORK && timeLeft <= 15 && timeLeft > 0 && (
                <p
                  className="text-[#FFD60A] font-bold tracking-[0.3em] uppercase mt-2 animate-pulse"
                  style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(11px, 2vw, 14px)' }}
                  data-testid="warning-label"
                >
                  ÚLTIMOS 15 SEGUNDOS
                </p>
              )}
            </div>
          )}

          {/* DONE */}
          {status === S.DONE && (
            <div className="text-center" data-testid="done-display">
              <div
                className="font-black uppercase text-[#32D74B] leading-tight"
                style={{ fontSize: 'clamp(56px, 18vw, 200px)', fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                CONCLUÍDO!
              </div>
              <p className="text-gray-500 mt-3 text-sm tracking-[0.3em] uppercase" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {cfg.rounds} round{cfg.rounds > 1 ? 's' : ''} completado{cfg.rounds > 1 ? 's' : ''}
              </p>
            </div>
          )}
        </main>

        {/* ── Controls ────────────────────────────────────────────────── */}
        <footer className="px-4 pb-10 pt-4">
          <div className="flex gap-3 max-w-lg mx-auto">

            {/* Start */}
            {status === S.IDLE && (
              <button
                data-testid="start-btn"
                onClick={handleStart}
                className="flex-1 bg-[#FF3B30] hover:bg-[#FF453A] active:scale-95 text-white font-black text-2xl py-6 rounded-md flex items-center justify-center gap-3 transition-all focus:outline-none focus:ring-2 focus:ring-[#FF3B30] focus:ring-offset-2 focus:ring-offset-black"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                <Play size={28} fill="white" strokeWidth={0} />
                INICIAR
              </button>
            )}

            {/* Pause */}
            {isActive && !paused && (
              <button
                data-testid="pause-btn"
                onClick={handlePause}
                className="flex-1 bg-[#2C2C2E] hover:bg-[#3A3A3C] active:scale-95 text-white font-black text-2xl py-6 rounded-md flex items-center justify-center gap-3 transition-all focus:outline-none focus:ring-2 focus:ring-white/30"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                <Pause size={28} />
                PAUSAR
              </button>
            )}

            {/* Resume */}
            {isActive && paused && (
              <button
                data-testid="resume-btn"
                onClick={handleResume}
                className="flex-1 bg-[#FF3B30] hover:bg-[#FF453A] active:scale-95 text-white font-black text-2xl py-6 rounded-md flex items-center justify-center gap-3 transition-all focus:outline-none focus:ring-2 focus:ring-[#FF3B30]"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                <Play size={28} fill="white" strokeWidth={0} />
                CONTINUAR
              </button>
            )}

            {/* Reset */}
            {(isActive || status === S.DONE) && (
              <button
                data-testid="reset-btn"
                onClick={handleReset}
                className="bg-[#2C2C2E] hover:bg-[#3A3A3C] active:scale-95 text-white font-black py-6 px-7 rounded-md flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-white/30"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                <RotateCcw size={26} />
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
